import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Zap, Save, Clock, RotateCcw, Wand2, Play, Pause, CalendarClock } from "lucide-react";
import { adminGetPricing, adminSetPricing, adminApplyPricing, adminApplySchedule } from "@/lib/api";

const MODE_LABELS = {
    listino: "🔵 LISTINO", prevendita: "🟢 PREVENDITA",
    happy_hour: "⚡ HAPPY HOUR", track_day: "🏁 TRACK DAY",
};
const MODE_COLORS = { listino: "#4B7BEA", prevendita: "#16a34a", happy_hour: "#f59e0b", track_day: "#E2231A" };

// V49 — Schedule automatica prezzi (Europe/Rome)
const SCHED_KEYS = ["listino", "prevendita", "track_day"];

const parseLocal = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d) ? null : d;
};
const fmt = (d) => d ? d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export function PricingTable({ token }) {
    const [data, setData] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [nowTick, setNowTick] = useState(Date.now());

    // Aggiorna timeline "adesso" ogni 30s
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    const load = async () => {
        try {
            const r = await adminGetPricing(token);
            // Backfill schedule locale se manca
            if (!r.schedule) r.schedule = { enabled: false, listino: { start: "", end: "" }, prevendita: { start: "", end: "" }, track_day: { start: "", end: "" } };
            setData(r); setDirty(false);
        }
        catch (e) { toast.error("Errore caricamento prezzi"); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    // Timeline Gantt (calcolato prima di early return per rispettare le rules of hooks)
    const gantt = useMemo(() => {
        if (!data) return null;
        const items = SCHED_KEYS.map(k => ({ mode: k, start: parseLocal(data.schedule?.[k]?.start), end: parseLocal(data.schedule?.[k]?.end) }))
            .filter(it => it.start && it.end && it.end > it.start);
        if (!items.length) return null;
        const min = new Date(Math.min(...items.map(i => i.start.getTime())));
        const max = new Date(Math.max(...items.map(i => i.end.getTime())));
        const totalMs = max - min;
        const pad = Math.max(totalMs * 0.05, 60 * 60 * 1000);
        const t0 = min.getTime() - pad;
        const t1 = max.getTime() + pad;
        const span = t1 - t0;
        const now = Date.now();
        const nowPct = ((now - t0) / span) * 100;
        const bars = items.map(it => ({
            mode: it.mode,
            leftPct: ((it.start.getTime() - t0) / span) * 100,
            widthPct: ((it.end.getTime() - it.start.getTime()) / span) * 100,
            start: it.start, end: it.end,
        }));
        const ticks = [];
        for (let i = 0; i <= 4; i++) {
            const t = t0 + (span * i) / 4;
            ticks.push({ leftPct: (i / 4) * 100, label: new Date(t).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) });
        }
        return { bars, ticks, nowPct: (nowPct >= 0 && nowPct <= 100) ? nowPct : null };
    }, [data, nowTick]); // eslint-disable-line

    if (!data) return <div className="text-[#9AA2AD] p-6">Caricamento prezzi…</div>;

    const setPkg = (idx, key, val) => {
        const next = { ...data, packages: data.packages.map((p, i) => i === idx ? { ...p, [key]: val } : p) };
        setData(next); setDirty(true);
    };
    const setNested = (idx, group, key, val) => {
        const next = {
            ...data, packages: data.packages.map((p, i) => i === idx ? { ...p, [group]: { ...p[group], [key]: val } } : p)
        };
        setData(next); setDirty(true);
    };
    const setSched = (mode, key, val) => {
        const next = { ...data, schedule: { ...data.schedule, [mode]: { ...(data.schedule[mode] || {}), [key]: val } } };
        setData(next); setDirty(true);
    };

    const save = async () => {
        setBusy(true);
        try { await adminSetPricing(token, data); toast.success("Prezzi salvati"); setDirty(false); }
        catch (e) { toast.error("Errore salvataggio"); }
        finally { setBusy(false); }
    };

    const applyMode = async (mode, duration_minutes = null) => {
        const label = MODE_LABELS[mode];
        const ok = window.confirm(duration_minutes
            ? `Attivare ${label} per ${duration_minutes} minuti?\nAlla scadenza tornerà automaticamente a PREVENDITA.`
            : `Attivare ${label} SUBITO come modalità corrente?\n\nATTENZIONE: se lo schedule automatico è attivo, questo override lo bypassa fino allo scadere.`);
        if (!ok) return;
        setBusy(true);
        try {
            await adminApplyPricing(token, mode, duration_minutes);
            toast.success(`${label} attiva!`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    const toggleSchedule = async () => {
        const enabled = !data.schedule?.enabled;
        if (dirty) {
            if (!window.confirm("Ci sono modifiche non salvate. Salvarle prima di attivare lo schedule?")) return;
            await save();
        }
        const missing = SCHED_KEYS.filter(k => !data.schedule?.[k]?.start || !data.schedule?.[k]?.end);
        if (enabled && missing.length === 3) {
            toast.error("Compila almeno una fascia (start + end) prima di attivare lo schedule");
            return;
        }
        setBusy(true);
        try {
            await adminApplySchedule(token, enabled);
            toast.success(enabled ? "✅ Schedule automatico ATTIVO" : "⏸ Schedule sospeso");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    // 🔧 Armonizza sovrapposizioni: rileva overlap tra fasce e le sistema consecutive
    const armonizza = () => {
        const sched = data.schedule || {};
        // Costruisco array di fasce valide, ordinato per start
        let items = SCHED_KEYS.map(k => ({ mode: k, start: parseLocal(sched[k]?.start), end: parseLocal(sched[k]?.end) }))
            .filter(it => it.start && it.end && it.end > it.start)
            .sort((a, b) => a.start - b.start);
        if (items.length < 2) {
            toast.info("Servono almeno 2 fasce compilate per armonizzare");
            return;
        }
        const changes = [];
        for (let i = 1; i < items.length; i++) {
            const prev = items[i - 1], curr = items[i];
            if (curr.start < prev.end) {
                // Overlap: sposto lo start di curr = end di prev
                const oldStart = curr.start;
                curr.start = new Date(prev.end.getTime());
                changes.push(`${curr.mode}: start ${fmt(oldStart)} → ${fmt(curr.start)}`);
                if (curr.end <= curr.start) {
                    // La fascia diventa invalida — la estendo di 1h di default
                    curr.end = new Date(curr.start.getTime() + 60 * 60 * 1000);
                    changes.push(`${curr.mode}: end esteso a ${fmt(curr.end)} (era troppo corto)`);
                }
            }
        }
        if (!changes.length) {
            toast.success("Nessuna sovrapposizione rilevata ✓");
            return;
        }
        if (!window.confirm(`Applicare queste modifiche?\n\n• ${changes.join("\n• ")}`)) return;
        const nextSched = { ...sched };
        const toLocalIso = (d) => {
            const p = (n) => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
        };
        items.forEach(it => {
            nextSched[it.mode] = { start: toLocalIso(it.start), end: toLocalIso(it.end) };
        });
        setData({ ...data, schedule: nextSched });
        setDirty(true);
        toast.success(`Armonizzate ${changes.length} sovrapposizioni — ricorda di Salvare`);
    };

    const pct = (base, prezzo) => {
        const b = parseFloat(base), p = parseFloat(prezzo);
        if (!b || !p || b <= p) return "—";
        return `-${Math.round((1 - p / b) * 100)}%`;
    };

    const activeMode = data.active_mode || "prevendita";
    const scheduleOn = !!data.schedule?.enabled;

    return (
        <div className="space-y-4" data-testid="pricing-table">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="uppercase tracking-widest text-sm text-[#F4F2ED]">📊 Tabella Prezzi Dinamica</h3>
                    <div className="text-[10px] text-[#9AA2AD]">Configura i 4 listini e attiva la modalità che preferisci · Fuso: Europe/Rome</div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Modalità attiva:</div>
                    <div className="px-3 py-1 text-xs uppercase tracking-widest border font-bold"
                        style={{ color: MODE_COLORS[activeMode], borderColor: MODE_COLORS[activeMode] }}
                        data-testid="pricing-active-mode">
                        {MODE_LABELS[activeMode]}
                    </div>
                    {scheduleOn && (
                        <div className="text-[10px] text-[#16a34a] uppercase tracking-widest border border-[#16a34a]/50 px-2 py-1" data-testid="pr-schedule-status">▶ Auto</div>
                    )}
                    {data.active_until && (
                        <div className="text-[10px] text-[#f59e0b]">
                            ⏰ fino a {new Date(data.active_until).toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                    )}
                </div>
            </div>

            {/* Timeline Gantt */}
            {gantt ? (
                <div className="border border-[#9AA2AD]/20 bg-[#0F1114] p-3" data-testid="pr-gantt">
                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-2 flex items-center gap-2">
                        <CalendarClock size={11} /> Timeline schedule (Europe/Rome)
                    </div>
                    <div className="relative h-14">
                        {/* barre modalità */}
                        {gantt.bars.map((b) => (
                            <div key={b.mode} className="absolute top-3 h-6 rounded-sm flex items-center px-2 overflow-hidden"
                                style={{ left: `${b.leftPct}%`, width: `${b.widthPct}%`, backgroundColor: MODE_COLORS[b.mode] + "40", border: `1px solid ${MODE_COLORS[b.mode]}` }}
                                title={`${MODE_LABELS[b.mode]} · ${fmt(b.start)} → ${fmt(b.end)}`}>
                                <span className="text-[9px] font-bold uppercase tracking-widest whitespace-nowrap" style={{ color: MODE_COLORS[b.mode] }}>
                                    {MODE_LABELS[b.mode]}
                                </span>
                            </div>
                        ))}
                        {/* indicatore ADESSO */}
                        {gantt.nowPct !== null && (
                            <div className="absolute top-0 bottom-3 w-px bg-[#F4F2ED]" style={{ left: `${gantt.nowPct}%` }} title="ADESSO">
                                <div className="absolute -top-2 -left-3 text-[8px] uppercase tracking-widest text-[#F4F2ED] bg-[#0F1114] px-1 border border-[#F4F2ED]">▼NOW</div>
                            </div>
                        )}
                    </div>
                    {/* Ticks assi */}
                    <div className="relative h-4 mt-1 border-t border-[#9AA2AD]/20">
                        {gantt.ticks.map((t, i) => (
                            <div key={i} className="absolute text-[8px] text-[#9AA2AD] font-mono whitespace-nowrap" style={{ left: `${t.leftPct}%`, transform: "translateX(-50%)" }}>
                                {t.label}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="text-[10px] text-[#9AA2AD] italic border border-dashed border-[#9AA2AD]/20 p-3 text-center">
                    Nessuna fascia schedulata — imposta le date sotto e premi <strong>Applica Schedule</strong>
                </div>
            )}

            {/* Fasce Schedule (globali) */}
            <div className="border border-[#9AA2AD]/20 bg-[#1C2026]/50 p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2">📅 Fasce automatiche (Europe/Rome)</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {SCHED_KEYS.map(k => (
                        <div key={k} className="border p-2" style={{ borderColor: MODE_COLORS[k] + "60" }} data-testid={`pr-sched-${k}`}>
                            <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: MODE_COLORS[k] }}>
                                {MODE_LABELS[k]}
                            </div>
                            <label className="block mb-2">
                                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Inizio (gg/mm/aaaa hh:mm)</div>
                                <input type="datetime-local" value={data.schedule?.[k]?.start || ""}
                                    onChange={e => setSched(k, "start", e.target.value)}
                                    className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-xs px-2 py-1.5 font-mono"
                                    data-testid={`pr-sched-${k}-start`} />
                            </label>
                            <label className="block">
                                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">Fine (gg/mm/aaaa hh:mm)</div>
                                <input type="datetime-local" value={data.schedule?.[k]?.end || ""}
                                    onChange={e => setSched(k, "end", e.target.value)}
                                    className="w-full bg-[#0F1114] border border-[#9AA2AD]/30 focus:border-[#4B7BEA] outline-none text-xs px-2 py-1.5 font-mono"
                                    data-testid={`pr-sched-${k}-end`} />
                            </label>
                        </div>
                    ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-[#9AA2AD]/10">
                    <button onClick={armonizza} disabled={busy}
                        className="border border-[#9333ea]/50 hover:bg-[#9333ea]/10 text-[#9333ea] px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-2"
                        data-testid="pr-armonizza">
                        <Wand2 size={12} /> Armonizza sovrapposizioni
                    </button>
                    <button onClick={toggleSchedule} disabled={busy}
                        className={`px-4 py-2 text-[11px] uppercase tracking-widest flex items-center gap-2 border font-bold ${scheduleOn ? "border-[#f59e0b] text-[#f59e0b] hover:bg-[#f59e0b]/10" : "border-[#16a34a] text-[#16a34a] hover:bg-[#16a34a]/10"}`}
                        data-testid="pr-toggle-schedule">
                        {scheduleOn ? <><Pause size={12} /> Sospendi Schedule</> : <><Play size={12} /> Applica Schedule</>}
                    </button>
                </div>
            </div>

            {/* Tabella prezzi */}
            <div className="border border-[#9AA2AD]/20 velora-scroll-x">
                <table className="w-full text-xs min-w-[1000px]">
                    <thead className="bg-[#1C2026] text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                        <tr>
                            <th className="text-left p-2">Pacchetto</th>
                            <th className="text-left p-2" style={{ color: MODE_COLORS.listino }}>Listino €</th>
                            <th className="text-left p-2" style={{ color: MODE_COLORS.prevendita }}>Prevendita €<br /><span className="text-[8px] font-normal normal-case">sconto vs Listino</span></th>
                            <th className="text-left p-2" style={{ color: MODE_COLORS.happy_hour }}>⚡ Happy Hour<br /><span className="text-[8px] font-normal normal-case">prezzo + fascia HH:MM</span></th>
                            <th className="text-left p-2" style={{ color: MODE_COLORS.track_day }}>🏁 Track Day €</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.packages.map((p, idx) => (
                            <tr key={p.key} className="border-t border-[#9AA2AD]/10">
                                <td className="p-2">
                                    <div className="text-[#F4F2ED] font-medium">{p.label}</div>
                                    <div className="text-[9px] text-[#9AA2AD] font-mono">{p.key}</div>
                                </td>
                                <td className="p-2">
                                    <input type="number" step="0.01" value={p.listino} onChange={e => setPkg(idx, "listino", parseFloat(e.target.value) || 0)}
                                        className="w-24 bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-2 py-1"
                                        data-testid={`pr-${p.key}-listino`} />
                                </td>
                                <td className="p-2">
                                    <input type="number" step="0.01" value={p.prevendita} onChange={e => setPkg(idx, "prevendita", parseFloat(e.target.value) || 0)}
                                        className="w-24 bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-2 py-1"
                                        data-testid={`pr-${p.key}-prev`} />
                                    <div className="text-[10px] mt-1" style={{ color: MODE_COLORS.prevendita }}>{pct(p.listino, p.prevendita)}</div>
                                </td>
                                <td className="p-2">
                                    <div className="flex items-center gap-1">
                                        <input type="number" step="0.01" value={p.happy_hour.prezzo} onChange={e => setNested(idx, "happy_hour", "prezzo", parseFloat(e.target.value) || 0)}
                                            className="w-20 bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-2 py-1"
                                            data-testid={`pr-${p.key}-hh-price`} />
                                        <input type="time" value={p.happy_hour.from} onChange={e => setNested(idx, "happy_hour", "from", e.target.value)}
                                            className="w-20 bg-[#0F1114] border border-[#9AA2AD]/20 text-[#9AA2AD] px-1 py-1 text-[10px]" />
                                        <span className="text-[#9AA2AD]">–</span>
                                        <input type="time" value={p.happy_hour.to} onChange={e => setNested(idx, "happy_hour", "to", e.target.value)}
                                            className="w-20 bg-[#0F1114] border border-[#9AA2AD]/20 text-[#9AA2AD] px-1 py-1 text-[10px]" />
                                    </div>
                                    <div className="text-[10px] mt-1" style={{ color: MODE_COLORS.happy_hour }}>{pct(p.listino, p.happy_hour.prezzo)}</div>
                                </td>
                                <td className="p-2">
                                    <input type="number" step="0.01" value={p.track_day.prezzo} onChange={e => setNested(idx, "track_day", "prezzo", parseFloat(e.target.value) || 0)}
                                        className="w-24 bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-2 py-1"
                                        data-testid={`pr-${p.key}-td-price`} />
                                    <div className="text-[10px] mt-1" style={{ color: MODE_COLORS.track_day }}>{pct(p.listino, p.track_day.prezzo)}</div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Bottoni azione */}
            <div className="flex flex-wrap gap-2 items-center border-t border-[#9AA2AD]/20 pt-3">
                <button onClick={save} disabled={!dirty || busy}
                    className="bg-[#4B7BEA] hover:bg-[#3866d6] disabled:opacity-40 text-white px-4 py-2 text-[11px] uppercase tracking-widest flex items-center gap-2"
                    data-testid="pr-save">
                    <Save size={13} /> {busy ? "Salvando…" : dirty ? "Salva Modifiche" : "Salvato ✓"}
                </button>
                <div className="w-px h-8 bg-[#9AA2AD]/20 mx-2" />
                <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mr-1">Override manuale:</div>
                <button onClick={() => applyMode("listino")} disabled={busy}
                    className="border border-[#4B7BEA]/40 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                    data-testid="pr-apply-listino"><Zap size={12} /> Listino</button>
                <button onClick={() => applyMode("prevendita")} disabled={busy}
                    className="border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                    data-testid="pr-apply-prev"><Zap size={12} /> Prevendita</button>
                <button onClick={() => applyMode("happy_hour", 60)} disabled={busy}
                    className="border border-[#f59e0b]/40 hover:bg-[#f59e0b]/10 text-[#f59e0b] px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                    data-testid="pr-apply-hh"><Clock size={12} /> ⚡ Happy Hour (60min)</button>
                <button onClick={() => applyMode("track_day")} disabled={busy}
                    className="border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                    data-testid="pr-apply-td"><Zap size={12} /> 🏁 Track Day</button>
                {data.active_mode !== "prevendita" && (
                    <button onClick={() => applyMode("prevendita")} disabled={busy}
                        className="ml-auto border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                        data-testid="pr-reset"><RotateCcw size={12} /> Reset</button>
                )}
            </div>

            <div className="border border-[#4B7BEA]/30 bg-[#4B7BEA]/5 p-3 text-[11px] text-[#F4F2ED]">
                💡 <strong>Come funziona:</strong> imposta le fasce <strong>Listino / Prevendita / Track Day</strong> con data-ora inizio/fine (Europe/Rome) → salva → premi <strong>Applica Schedule</strong> per attivare la commutazione automatica. Se nessuna fascia copre "adesso", il prezzo di <strong>Prevendita</strong> resta attivo di default. I bottoni sotto sono override manuali immediati. <strong>⚡ Happy Hour</strong> torna automaticamente a Prevendita dopo 60 minuti. Il tasto <strong>🔧 Armonizza</strong> sistema in automatico gli slot sovrapposti rendendoli consecutivi.
            </div>
        </div>
    );
}
